import { fetchApiGateway } from "@nova/fetch-api-gateway"
import {
    boundsIntersect,
    getMultiPolygonBounds,
    multiPolygonWithHolesToGeoJSON,
} from "@nova/calculate-geospatial"
import { selectActiveWorkspaceId } from "features/data-management/workspace/WorkspaceManagementSlice"
import store from "integrations/redux/store"
import { getViewport, getViewportBounds } from "features/map/GlobalViewState"
import { createExternalStore } from "@nova/toolkit-pure-hooks"
import { getMinShapeArea, getPaddedViewportBounds, getSimplificationTolerance } from "../../zoomLod"
import { CachedAirspace } from "../../types"
import { SupportedRegions } from "@nova/constants"

const MAX_RESTRICTION_ENTRY_COUNT = 2000

const { set, prune, get, useAll, useItems, useItem } = createExternalStore<CachedAirspace>({
    maxEntries: MAX_RESTRICTION_ENTRY_COUNT,
})
export const useRestrictionsCache = useAll
export const useRestrictions = useItems
export const useRestriction = useItem

export const fetchAndCacheRestrictions = ({
    abortController,
}: {
    abortController: AbortController
}) => {
    const orgId = selectActiveWorkspaceId()(store.getState())
    if (!orgId) return
    const bounds = getViewportBounds()
    const viewport = getViewport()
    const zoom = viewport.zoom
    const simplificationTolerance = getSimplificationTolerance(zoom)
    const minShapeArea = getMinShapeArea(zoom)
    const excludeIds = [...get()]
        .filter(
            ([, cached]) =>
                cached.simplificationTolerance <= simplificationTolerance &&
                boundsIntersect(cached.bbox, getPaddedViewportBounds(viewport))
        )
        .map(([id]) => id)
    fetchApiGateway("post /org/:orgId/restrictions", {
        params: { orgId },
        body: {
            bounds,
            simplificationTolerance,
            minShapeArea,
            excludeIds,
        },
        region: SupportedRegions.Canada,
        signal: abortController.signal,
    })
        .then((res) => {
            const returnedIds: string[] = []
            for (const airspace of res.airspaces) {
                if (!airspace.id) continue
                const id = `${airspace.id}`
                returnedIds.push(id)
                const geometry = multiPolygonWithHolesToGeoJSON(airspace.geometry)
                set(id, {
                    type: "restriction",
                    feature: { type: "Feature", id, geometry, properties: airspace.properties },
                    simplificationTolerance,
                    properties: airspace.properties,
                    bbox: getMultiPolygonBounds(airspace.geometry),
                    area: airspace.properties.area,
                })
            }
            prune([...excludeIds, ...returnedIds])
        })
        .catch((err) => {
            if (err?.name !== "AbortError") {
                console.warn("[restriction] fetch failed:", err)
            }
        })
}
